import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useOrigen } from '../../contexts/OrigenContext';
type Arrow = 'left' | 'right' | 'top' | 'bottom';
const arrowWrapper: Record<Arrow, string> = {
  left: '-left-[6px] top-1/2 -translate-y-1/2',
  right: '-right-[6px] top-1/2 -translate-y-1/2',
  top: '-top-[6px] left-1/2 -translate-x-1/2',
  bottom: '-bottom-[6px] left-1/2 -translate-x-1/2'
};
const arrowShape: Record<Arrow, {
  viewBox: string;
  points: string;
  size: string;
}> = {
  left: {
    viewBox: '0 0 6 8',
    points: '6,0 0,4 6,8',
    size: 'h-2 w-[6px]'
  },
  right: {
    viewBox: '0 0 6 8',
    points: '0,0 6,4 0,8',
    size: 'h-2 w-[6px]'
  },
  top: {
    viewBox: '0 0 8 6',
    points: '4,0 8,6 0,6',
    size: 'h-[6px] w-2'
  },
  bottom: {
    viewBox: '0 0 8 6',
    points: '0,0 8,0 4,6',
    size: 'h-[6px] w-2'
  }
};
function ChipArrow({
  arrow
}: {
  arrow: Arrow;
}) {
  const shape = arrowShape[arrow];
  return <svg viewBox={shape.viewBox} aria-hidden="true" focusable="false" className={`absolute text-primary ${shape.size} ${arrowWrapper[arrow]}`} data-id="element-200" data-mp="ilcqvmwh3nc0">
      <polygon points={shape.points} fill="currentColor" data-id="element-201" data-mp="4xfrjjpmej8h" />
    </svg>;
}
function ChipBody({
  children
}: {
  children: React.ReactNode;
}) {
  return <>
      <span className="flex items-center gap-2" data-id="element-202" data-mp="iwoyrtvum30b">
        <span className="h-[7px] w-[7px] rounded-full bg-secondary" aria-hidden="true" data-id="element-203" data-mp="dydoji1n5qhk" />
        <span className="font-sans text-[10px] font-bold uppercase tracking-[0.14em] text-secondary" data-id="element-204" data-mp="3mdxdw5z6yorc">
          Criterio ORIGEN
        </span>
      </span>
      <p className="mt-2 font-sans text-[12px] leading-[1.5] text-white/85" data-id="element-205" data-mp="3idwzumg08fkt">{children}</p>
    </>;
}
const chipBase = 'w-[240px] rounded-lg bg-primary px-[14px] py-[10px] shadow-chip';
type OrigenChipProps = {
  /**
   * anchored: se ancla al elemento anotado en desktop (usar `position`).
   * flow: se muestra centrado debajo del elemento, en el flujo del documento.
   */
  layout?: 'anchored' | 'flow';
  /** Clases de posicionamiento absoluto para desktop (solo layout anchored) */
  position?: string;
  /** Índice para la animación escalonada */
  order?: number;
  arrow?: Arrow;
  /** Sección de fondo oscuro: añade borde aqua para distinguir el chip */
  dark?: boolean;
  /** Clases extra para el chip en modo flow (alineación, márgenes) */
  className?: string;
  children: React.ReactNode;
};
export function OrigenChip({
  layout = 'anchored',
  position = '',
  order = 0,
  arrow = layout === 'flow' ? 'top' : 'left',
  dark = false,
  className = '',
  children
}: OrigenChipProps) {
  const {
    active
  } = useOrigen();
  const border = dark ? 'border border-secondary' : '';
  const enter = {
    opacity: 1,
    y: 0,
    transition: {
      delay: order * 0.1,
      duration: 0.32,
      ease: 'easeOut'
    }
  };
  const exit = {
    opacity: 0,
    transition: {
      duration: 0.15
    }
  };
  if (layout === 'flow') {
    return <AnimatePresence data-id="element-206" data-mp="24z5itof2ztcc">
        {active && <motion.aside initial={{
        opacity: 0,
        y: 12
      }} animate={enter} exit={exit} className={`relative z-40 mx-auto mt-4 ${chipBase} ${border} ${className}`} data-id="element-207" data-mp="2910jqrt3o4pl">
            <ChipBody data-id="element-208" data-mp="3phhepbll6j80">{children}</ChipBody>
            <ChipArrow arrow={layout === 'flow' ? arrow : 'top'} data-id="element-209" data-mp="3pij1gdbysu5k" />
          </motion.aside>}
      </AnimatePresence>;
  }
  return <AnimatePresence data-id="element-210" data-mp="3fsg1tuac8hy9">
      {active && <>
          {/* Desktop: anclado al elemento anotado, sin taparlo */}
          <motion.aside key="desktop" initial={{
        opacity: 0,
        y: 14
      }} animate={enter} exit={exit} className={`pointer-events-none absolute z-40 hidden lg:block ${chipBase} ${border} ${position}`} data-id="element-211" data-mp="3atwb7gksuhzk">
            <ChipBody data-id="element-212" data-mp="2wtbuqr2cwy98">{children}</ChipBody>
            <ChipArrow arrow={arrow} data-id="element-213" data-mp="2wumy4prw43zt" />
          </motion.aside>

          {/* Móvil: en el flujo, debajo del elemento */}
          <motion.aside key="mobile" initial={{
        opacity: 0,
        y: 10
      }} animate={enter} exit={exit} className={`relative z-40 mt-5 block w-full max-w-[320px] rounded-lg bg-primary px-[14px] py-[10px] shadow-chip lg:hidden ${border}`} data-id="element-214" data-mp="zypgtkdqf6dd">
            <ChipBody data-id="element-215" data-mp="3st63geir955u">{children}</ChipBody>
          </motion.aside>
        </>}
    </AnimatePresence>;
}